/**
 * Real API client for the Stamp Admin API.
 * Replaces mockData.ts with live calls to stamp-admin-api.calitoy.workers.dev
 */

const API_BASE = 'https://stamp-admin-api.calitoy.workers.dev/api';

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  const data = await res.json();
  if (!res.ok || data.ok === false) {
    throw new Error(data.error?.message || data.error || `API error: ${res.status}`);
  }
  return (data.data ?? data) as T;
}

// ============ Types ============

export type JobStatus =
  | 'brief_draft' | 'generating' | 'artwork_review' | 'artwork_approved'
  | 'product_configuration' | 'mockups_processing' | 'merch_review'
  | 'sample_required' | 'ready_to_publish' | 'publishing' | 'published'
  | 'action_required' | 'rejected' | 'archived';

export type Brand = 'Calitoy' | 'Kurced' | 'CalitoyMuse' | 'Custom';

export type IntendedUse =
  | 'chest_mark' | 'full_front' | 'back_print' | 'sleeve'
  | 'embroidery' | 'sticker';

export type CatalogProductStatus = 'draft' | 'published' | 'archived' | 'action_required';

export interface Brief {
  brand: Brand;
  logoPrompt: string;
  referenceImage?: string;
  requiredText?: string;
  style?: string;
  colors?: string[];
  exclusions?: string[];
  intendedUse: IntendedUse;
  garmentTypes?: string[];
  garmentColors?: string[];
  conceptCount: number;
}

export interface ArtworkCandidate {
  id: string;
  url: string;
  label: string;
}

export interface Job {
  id: string;
  brand: Brand;
  brief: Brief;
  status: JobStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
  artwork?: ArtworkCandidate[];
  products?: ProductDraft[];
  mockups?: MockupResult[];
}

export interface ProductDraft {
  id: string;
  job_id: string;
  printful_product_id: number;
  printful_product_name: string;
  technique: string;
  placement: string;
  status: string;
  variants: VariantDraft[];
}

export interface VariantDraft {
  id: number;
  name: string;
  color: string;
  size: string;
  enabled: boolean;
  cost: number;
  retail_price: number;
}

export interface MockupResult {
  id: string;
  url: string;
  label: string;
  status: 'pending' | 'processing' | 'complete' | 'failed';
}

export interface CatalogProduct {
  id: string;
  slug: string;
  title: string;
  description: string;
  brand: Brand;
  collection: string;
  status: CatalogProductStatus;
  price: number;
  image: string;
  variants_count: number;
}

export interface PrintfulCatalogProduct {
  id: number;
  name: string;
  type: string;
  image: string;
  variants: PrintfulVariant[];
}

export interface PrintfulVariant {
  id: number;
  name: string;
  color: string;
  size: string;
  image: string;
  price: string;
}

// ============ API functions ============

export async function fetchJobs(): Promise<Job[]> {
  try {
    return await api<Job[]>('/jobs');
  } catch {
    return [];
  }
}

export async function fetchJob(id: string): Promise<Job | null> {
  try {
    return await api<Job>(`/jobs/${id}`);
  } catch {
    return null;
  }
}

export async function createJob(brief: Brief): Promise<Job> {
  return await api<Job>('/jobs', {
    method: 'POST',
    body: JSON.stringify({ ...brief, created_by: 'admin' }),
  });
}

export async function updateJobStatus(id: string, status: JobStatus): Promise<Job> {
  return await api<Job>(`/jobs/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function fetchProducts(): Promise<CatalogProduct[]> {
  try {
    return await api<CatalogProduct[]>('/catalog/products');
  } catch {
    return [];
  }
}

export async function fetchPrintfulCatalog(): Promise<PrintfulCatalogProduct[]> {
  try {
    const res = await api<{ products: PrintfulCatalogProduct[] }>('/printful/catalog', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return res.products || [];
  } catch {
    return [];
  }
}

export async function generateArtwork(prompt: string, brand: string): Promise<ArtworkCandidate[]> {
  try {
    const res = await api<{ candidates: ArtworkCandidate[] }>('/artwork/generate', {
      method: 'POST',
      body: JSON.stringify({ prompt, brand }),
    });
    return res.candidates || [];
  } catch {
    return [];
  }
}

export async function createMockupTask(productId: number, variantIds: number[], imageUrl: string, placement: string): Promise<{ task_id: string }> {
  return await api<{ task_id: string }>('/printful/create-mockup', {
    method: 'POST',
    body: JSON.stringify({ productId, variantIds, imageUrl, placement }),
  });
}

export async function getMockupTask(taskId: string): Promise<{ status: string; mockups: MockupResult[] }> {
  return await api<{ status: string; mockups: MockupResult[] }>(`/printful/mockup-task/${taskId}`);
}

export async function publishDraft(draftId: string): Promise<{ success: boolean }> {
  return await api<{ success: boolean }>('/publish', {
    method: 'POST',
    body: JSON.stringify({ draftId }),
  });
}

export async function createStripeProduct(name: string, description: string, amount: number, imageUrl?: string): Promise<{ id: string; price_id: string }> {
  return await api<{ id: string; price_id: string }>('/stripe/create-product', {
    method: 'POST',
    body: JSON.stringify({ name, description, amount, imageUrl }),
  });
}

export interface StampSettings {
  store_ids: { name: string; id: number }[];
  default_store_id: number;
  pricing_rules: { default_markup_pct: number; min_margin_pct: number; currency: string };
}

export async function fetchSettings(): Promise<StampSettings> {
  return await api<StampSettings>('/settings');
}

export async function updateSettings(settings: Partial<StampSettings>): Promise<StampSettings> {
  return await api<StampSettings>('/settings', {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });
}

// ============ Mock fallbacks (for components that still need static data) ============

export const MOCK_PRINTFUL_PRODUCTS: PrintfulCatalogProduct[] = [];
export const MOCK_VARIANTS: PrintfulVariant[] = [];
export const MOCK_MOCKUPS: MockupResult[] = [];

// ============ Status helpers ============

export const ALL_STATUSES: JobStatus[] = [
  'brief_draft', 'generating', 'artwork_review', 'artwork_approved',
  'product_configuration', 'mockups_processing', 'merch_review',
  'sample_required', 'ready_to_publish', 'publishing', 'published',
  'action_required', 'rejected', 'archived',
];

export const STAGE_ORDER: JobStatus[] = [
  'brief_draft', 'generating', 'artwork_review', 'artwork_approved',
  'product_configuration', 'mockups_processing', 'merch_review',
  'sample_required', 'ready_to_publish', 'publishing', 'published',
];

export function statusLabel(status: JobStatus): string {
  const labels: Record<JobStatus, string> = {
    brief_draft: 'Brief Draft',
    generating: 'Generating',
    artwork_review: 'Artwork Review',
    artwork_approved: 'Artwork Approved',
    product_configuration: 'Product Config',
    mockups_processing: 'Mockups',
    merch_review: 'Final Review',
    sample_required: 'Sample Required',
    ready_to_publish: 'Ready to Publish',
    publishing: 'Publishing',
    published: 'Published',
    action_required: 'Action Required',
    rejected: 'Rejected',
    archived: 'Archived',
  };
  return labels[status] || status;
}
